/* eslint-disable prettier/prettier */

import { PropsWithChildren, useEffect, useMemo, useRef, useContext } from 'react';

import TagManager from 'react-gtm-module';
import { NavigateFunction, useLocation, useNavigate } from 'react-router-dom';

import { TrackingAnalyticsContext, ITrackingAnalyticsContext } from './context';
import { googleTagManagerInitializeArgs, googleEventIdMap, facebookPixelId, shouldTrack } from './config';
import { TrackingEventsEnum } from 'types/trackingAnalytics';

export function useTrackingAnalytics() {
    const context = useContext(TrackingAnalyticsContext);
    if (!context) {
        throw new Error('useTrackingAnalytics must be used within TrackingAnalyticsProvider');
    }
    return context.trackEvent;
}

// Facebook Pixel Code
function initializeFacebookPixel(f: any, b: any, e: string, v: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (f.fbq) resolve();
    const n: any = (f.fbq = function (...args: any[]) {
      n.callMethod
        ? n.callMethod(...args)
        : n.queue.push(args);
    });
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    const t = b.createElement(e);
    t.async = true;
    t.src = v;
    const s = b.getElementsByTagName(e)[0];
    s.parentNode!.insertBefore(t, s);
    t.onload = resolve;
    t.onerror = reject;
  });
}

interface TrackingAnalyticsProviderProps extends PropsWithChildren {}

export function TrackingAnalyticsProvider({ children }: TrackingAnalyticsProviderProps) {
  const eventQueueRef = useRef<Array<() => void>>([]);
  const fbPixelPromise = useRef<Promise<void> | null>(null);

  const enqueueEvent = (event: () => void) => {
    eventQueueRef.current = [...eventQueueRef.current, event];
  };

  const eventHandlers: { [event: string]: (data: any | null) => void | null } = useMemo(
    () => ({
      [TrackingEventsEnum.CompleteRegistration]: (data: any) => {
        (window as any).fbq('track', 'CompleteRegistration');
        TagManager.dataLayer({
          dataLayer: { event: 'conversion', send_to: googleEventIdMap[TrackingEventsEnum.CompleteRegistration] },
        });
      },
      [TrackingEventsEnum.InitiateCheckout]: () => {
        (window as any).fbq('track', 'InitiateCheckout');
        TagManager.dataLayer({
          dataLayer: { event: 'conversion', send_to: googleEventIdMap[TrackingEventsEnum.InitiateCheckout] },
        });
      },
      [TrackingEventsEnum.CompleteCheckout]: (data: any) => {
        (window as any).fbq('track', 'Purchase', data);
        TagManager.dataLayer({
          dataLayer: { event: 'conversion', send_to: googleEventIdMap[TrackingEventsEnum.CompleteCheckout], ...data },
        });
      },
      [TrackingEventsEnum.Recaptcha_Clicked]: () => {
        console.log('📌 reCAPTCHA Clicked Event Tracked');
        TagManager.dataLayer({
          dataLayer: {
            event: 'recaptcha_click',
            send_to: googleEventIdMap[TrackingEventsEnum.Recaptcha_Clicked],
          },
        });
      },
      [TrackingEventsEnum.Recaptcha_Success]: () => {
        console.log('✅ reCAPTCHA Success Event Tracked');
        TagManager.dataLayer({
          dataLayer: {
            event: 'recaptcha_success',
            send_to: googleEventIdMap[TrackingEventsEnum.Recaptcha_Success],
          },
        });
      },
      [TrackingEventsEnum.Recaptcha_Failed]: (data: any) => {
        console.error('❌ reCAPTCHA Failed:', data?.error);
        TagManager.dataLayer({
          dataLayer: {
            event: 'recaptcha_failed',
            error_message: data?.error,
            send_to: googleEventIdMap[TrackingEventsEnum.Recaptcha_Failed],
          },
        });
      },
      default: () => {
        console.error('Unhandled event name');
      },
    }),
    [],
  );

  const trackEvent = (event: TrackingEventsEnum, details?: { [propName: string]: string | number }): void => {
    if (!shouldTrack) {
      console.log(`Skipped tracking event "${event}" — not on hipaalink.net`);
      return;
    }

    const trackingEvent = () => (eventHandlers[event] ?? eventHandlers.default)(details);

    if (fbPixelPromise.current) {
      trackingEvent();
    } else {
      enqueueEvent(trackingEvent);
    }
  };

  useEffect(() => {
    if (facebookPixelId) {
      fbPixelPromise.current = initializeFacebookPixel(
        window,
        document,
        'script',
        'https://connect.facebook.net/en_US/fbevents.js',
      ).then(() => {
        (window as any).fbq('init', facebookPixelId);
      });

      Promise.allSettled([fbPixelPromise.current]).then(() => {
        eventQueueRef.current.forEach((event) => event());
        eventQueueRef.current = [];
      });
    } else {
      console.log('Facebook Pixel skipped – not running on hipaalink.net');
    }

    if (googleTagManagerInitializeArgs.gtmId) {
      TagManager.initialize(googleTagManagerInitializeArgs);
    } else {
      console.log('GTM skipped – not running on hipaalink.net');
    }
  }, []);

  return (
    <TrackingAnalyticsContext.Provider value={{ trackEvent } as ITrackingAnalyticsContext}>
      {children}
    </TrackingAnalyticsContext.Provider>
  );
}
